import React, { useState } from "react";
import type { ContentBlock } from "../types/messages";
import { MultimodalPreview } from "./MultimodalPreview";
import { ImageDescriptionDialog } from "./ImageDescriptionDialog";
import { cn } from "@/lib/utils";

interface ContentBlocksPreviewProps {
  blocks: ContentBlock[];
  onRemove: (idx: number) => void;
  onUpdateDescription?: (idx: number, description: string, descriptionAudioUrls?: string[]) => void;
  size?: "sm" | "md" | "lg";
  className?: string;
  // Variant: "chat" adds p-4 padding, "capture" has no padding
  variant?: "chat" | "capture";
}

/**
 * Renders a preview of content blocks with optional remove functionality.
 * Audio blocks are rendered full-width, other blocks use flex-wrap layout.
 * Images can be clicked to add descriptions.
 */
export const ContentBlocksPreview: React.FC<ContentBlocksPreviewProps> = ({
  blocks,
  onRemove,
  onUpdateDescription,
  size = "md",
  className,
  variant = "chat",
}) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);

  if (!blocks.length) return null;

  // Separate audio blocks (full-width) from other blocks (flex-wrap)
  const audioBlocks = blocks.filter((b) => b.type === "audio");
  const otherBlocks = blocks.filter((b) => b.type !== "audio");

  const handleImageClick = (blockIndex: number) => {
    if (onUpdateDescription) {
      setSelectedImageIndex(blockIndex);
    }
  };

  const handleSaveDescription = (description: string, descriptionAudioUrls?: string[]) => {
    if (selectedImageIndex !== null && onUpdateDescription) {
      onUpdateDescription(selectedImageIndex, description, descriptionAudioUrls);
    }
    setSelectedImageIndex(null);
  };

  const selectedBlock = selectedImageIndex !== null ? blocks[selectedImageIndex] : null;

  return (
    <div className={cn("", className)}>
      {/* Audio blocks - full width, stacked, numbered if more than one */}
      {audioBlocks.map((block, idx) => (
        <MultimodalPreview
          key={`audio-${idx}`}
          block={block}
          removable
          onRemove={() => onRemove(blocks.indexOf(block))}
          label={audioBlocks.length > 1 ? idx + 1 : undefined}
          variant="full"
        />
      ))}

      {/* Other blocks (images, PDFs) - flex wrap */}
      {otherBlocks.length > 0 && (
        <div className={cn("flex flex-wrap gap-2", variant === "chat" && "p-4")}>
          {otherBlocks.map((block, idx) => {
            const blockIndex = blocks.indexOf(block);
            const isImage = block.type === "image";
            return (
              <MultimodalPreview
                key={`other-${idx}`}
                block={block}
                removable
                onRemove={() => onRemove(blockIndex)}
                onImageClick={isImage && onUpdateDescription ? () => handleImageClick(blockIndex) : undefined}
                size={size}
              />
            );
          })}
        </div>
      )}

      {/* Image description dialog */}
      <ImageDescriptionDialog
        open={selectedImageIndex !== null}
        onOpenChange={(open) => {
          if (!open) setSelectedImageIndex(null);
        }}
        block={selectedBlock}
        onSave={handleSaveDescription}
      />
    </div>
  );
};
